import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import Ratings from "react-ratings-declarative";
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import { QuestionResponse, Survey, surveysApi } from "../../api";
import Loader from "../../common/components/Loader";

const SurveyModal: React.FC = () => {

  const isMobile = useMediaQuery({
    query: "(max-width: 386px)",
  });

  const [survey, setSurvey] = useState<Survey>({ id: "", questions: [] });
  const [responses, setResponses] = useState<QuestionResponse[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);
  const isValid = score !== 0;
  const totalQuestions = survey.questions.length;
  const isLastQuestion = questionIndex === survey.questions.length - 1;

  useEffect(() => {
    initAsync();
  }, []);

  useEffect(() => {
    const item = responses[questionIndex];
    if (item) {
      setScore(item.score);
      setComment(item.details ?? "");
    }
  }, [questionIndex]);

  useEffect(() => {
    if (isValid) {
      setShowErrors(false);
    }
  }, [isValid]);

  const initAsync = async () => {
    let survey;
    try {
      const response = await surveysApi.getCurrentSurvey();
      survey = response.data;
    }
    catch { }
    if (survey) {
      setSurvey(survey);
      setResponses(survey.questions.map(x => ({
        questionId: x.id,
        score: 0,
      })));
      open();
    }
  };

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  const begin = () => {
    setIsStarted(true);
  };

  const setRating = (value: number) => {
    setScore(value);
  };

  const getResponses = (arr: QuestionResponse[]) =>
    arr.map((x, i) => i === questionIndex ? { ...x, score, details: comment } : x);

  const update = () => {
    setResponses(getResponses);
  };

  const page = (delta: number) => {
    update();
    setQuestionIndex(index => index + delta);
  };

  const back = () => {
    page(-1);
  };

  const next = () => {
    if (isValid) {
      page(1);
    }
    else {
      setShowErrors(true);
    }
  };

  const submitAsync = async () => {
    if (isValid) {
      setIsSubmitting(true);
      const data = getResponses(responses);
      update();
      try {
        await surveysApi.createSurveyResponse({
          body: {
            surveyId: survey.id,
            responses: data,
          }
        });
      }
      catch {
        setIsSubmitError(true);
      }
      setIsSubmitting(false);
      setIsCompleted(true);
    }
    else {
      setShowErrors(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      centered
      size={isStarted && !isCompleted ? "lg" : "md"}
    >
      {isCompleted
        ? (
          <>
            <ModalBody className="text-center fs-7">
              {!isSubmitError
                ? "Thanks for your feedback!"
                : "Sorry, something went wrong."}
            </ModalBody>
            <ModalFooter className="border-0 justify-content-center">
              <Button
                color="primary"
                onClick={close}
              >
                Close
              </Button>
            </ModalFooter>
          </>)
        : isStarted
          ? (
            <>
              <ModalHeader tag="div" className="border-0 text-none">
                <div className="fs-8 text-secondary">{survey.questions[questionIndex].text}</div>
                <div className="fs-9 text-muted">{questionIndex + 1} of {totalQuestions}</div>
              </ModalHeader>
              <ModalBody>
                <div className="d-flex flex-column align-items-center">
                  <Ratings
                    rating={score}
                    changeRating={setRating}
                    widgetDimensions={isMobile ? "34px" : "45px"}
                    widgetSpacings={isMobile ? "3px" : "10px"}
                    widgetRatedColors={"#e6cc00"}
                    widgetHoverColors={"#e6cc00"}
                  >
                    <Ratings.Widget />
                    <Ratings.Widget />
                    <Ratings.Widget />
                    <Ratings.Widget />
                    <Ratings.Widget />
                  </Ratings>
                  {showErrors &&
                    <div className="mt-2 text-danger">
                      Please give a score
                    </div>
                  }
                </div>
                <Container fluid className="mt-4">
                  <Row>
                    <Col xs={{ offset: 0 }} lg={{ offset: 2, size: 8 }}>
                      <Label for="SurveyQuestionComments" className="text-secondary">Comments (optional)</Label>
                      <Input
                        onChange={e => setComment(e.target.value)}
                        value={comment}
                        type="textarea"
                        id="SurveyQuestionComments"
                        style={{
                          minHeight: "10rem",
                          resize: "none",
                        }}
                      />
                    </Col>
                  </Row>
                </Container>
              </ModalBody>
              <ModalFooter className="border-0 justify-content-between">
                {/* Back */}
                <Button
                  color="neutral"
                  disabled={questionIndex === 0}
                  onClick={back}
                >
                  Back
                </Button>
                {/* Cancel */}
                <Button
                  color="neutral"
                  onClick={close}
                >
                  Cancel
                </Button>
                {/* Next / Submit */}
                <Button
                  color="primary"
                  disabled={showErrors || isSubmitting}
                  onClick={isLastQuestion ? submitAsync : next}
                >
                  {isLastQuestion ? "Submit" : "Next"}
                </Button>
              </ModalFooter>
              <Loader show={isSubmitting}></Loader>
            </>)
          : (
            <>
              <ModalHeader className="border-0">We'd love your feedback!</ModalHeader>
              <ModalBody>
                Please answer a few short questions about your experience.
              </ModalBody>
              <ModalFooter className="border-0">
                <Button
                  color="primary"
                  onClick={begin}
                >
                  Yes
                </Button>
                <Button
                  color="neutral"
                  onClick={close}>
                  No
                </Button>
              </ModalFooter>
            </>)}
    </Modal>
  );
};

export default SurveyModal;
